/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from 'react-router-dom';
import {CustomHeaderWithOutAppBar} from "../../components/header/index.jsx";
import plantumlEncoder from "plantuml-encoder";
import './designagent.scss';
import { Container, Col, Row } from 'react-bootstrap';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useNavigate } from 'react-router-dom';
import url, { backendUrl } from "../../config.js"
import socketIOClient from "socket.io-client";
import { v4 as uuidv4 } from 'uuid';
import ZoomableDiagram from '../../components/zoomableDiagram/index.jsx';
import ConfluenceSection from "../../components/confluence/index.jsx";
import ConsolePromptPanel from '../../components/consolePromptPanel/index.jsx';

const samplePrompts = [
  {
    id: 1,
    category: 'Mule Payment Integration',
    title: 'Payment Processing with Stripe & Queue',
    prompt: `Create a sequence diagram for payment processing with SITS, MuleAPI, Queue, StripeAPI, DLQ, FinanceSystem, and AggressoFee. SITS pushes payment details to MuleAPI. MuleAPI publishes a payment event to Queue. MuleAPI calls StripeAPI to process the payment. On success, payment details are inserted into FinanceSystem and AggressoFee. On failure, an error message is pushed to DLQ. If the transaction commits, inserts are committed. If it rolls back, an error is sent to DLQ.`
  },
  {
    id: 2,
    category: 'Mule AI Agent',
    title: 'Slack AI Agent with ERP Integration',
    prompt: `Create a sequence diagram for a user interact with an AI agent and this AI agent is served in Slack. AI agent will act as an assistant to the user and validates the request with Business Rules stored in Data cloud. Once the validation is successful agent then sends the request to MuleSoft API and this will then create the POR in ERP.`
  },
  {
    id: 3,
    category: 'Mule Credit Card Application',
    title: 'Premium Business Credit Card Processing',
    prompt: `Create an integration design for a Premium Business Credit Card application process for FirstBank:

REQUIREMENTS:
Application submitted through Salesforce from relationship manager
Automatic credit check with Experian (business & personal guarantor)
Revenue verification from bank account statements (last 12 months)
If requested credit > $100K, require CFO approval via Slack
If business in high-risk industry (cannabis, crypto, gambling), route to Legal Compliance team
Fraud screening against OFAC sanctions list
Real-time decision engine with tiered approval matrix:
  * < $50K: Auto-approve if credit score > 720
  * $50K-$100K: Senior underwriter approval
  * > $100K: Credit committee approval
Approved applications trigger:
  * DocuSign for credit agreement
  * Card fulfillment system notification
  * Welcome email with account details
All decisions logged to Data Lake for audit/compliance
SLA: Decision within 4 hours for standard applications

INTEGRATIONS NEEDED:
Salesforce CRM, Experian Credit API, OFAC Screening Service, Slack (approval workflows), DocuSign, Core Banking System (card issuance), Email service (SendGrid), AWS S3 Data Lake (audit logs)`
  }
];
const domain = url.domain;

function DesignAgent() {
  let { projId, projName } = useParams();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const hasInitialized = useRef(false);

  const [pageName, setPageName] = useState('');
  const [state, setState] = useState({
    chat: '',
    diagramResult: null,
    description: '',
    diagramError: null,
    processingTime: null,
    components: [],
    interactions: [],
    documentation: '',
    html: '',
    markdown: '',
    loading: false,
    requestCount: 0,
    sessionHistory: [],
    activeView: 'diagram',
    plantumlCode: ''
  });

  // ✅ NEW: Console logs state (like integration agent)
  const [designLogs, setDesignLogs] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [confluenceAccounts, setConfluenceAccounts] = useState([]);
  const [showConfluenceDropdown, setShowConfluenceDropdown] = useState(false);
  const [confluenceSpaceName, setConfluenceSpaceName] = useState('INTELOG-TEST-SPACE');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [creationResult, setCreationResult] = useState(null);
  const [creationError, setCreationError] = useState(null);

  const handleCodeUpdate = (newCode) => {
    setState(prev => ({
      ...prev,
      plantumlCode: newCode
    }));
  };

  const handleReset = () => {
    setSelectedAccount(null);
    setConfluenceSpaceName('');
    setPageName('');
    setCreationResult(null);
    setCreationError(null);
    setShowConfluenceDropdown(false);
  };

  const addToSessionHistory = (request, result, error = null) => {
    const historyItem = {
      id: Date.now(),
      timestamp: new Date(),
      request: request,
      success: !error,
      processingTime: result?.processingTime,
      diagramType: result?.data?.diagramType,
      componentCount: result?.data?.components?.length || 0,
      error: error
    };

    setState(prevState => ({
      ...prevState,
      sessionHistory: [historyItem, ...prevState.sessionHistory.slice(0, 9)]
    }));

    const currentHistory = JSON.parse(localStorage.getItem('langraph_session_history') || '[]');
    const updatedHistory = [historyItem, ...currentHistory.slice(0, 9)];
    localStorage.setItem('langraph_session_history', JSON.stringify(updatedHistory));
  };

  const copyToClipboard = async () => {
    if (!state.plantumlCode) return;

    try {
      await navigator.clipboard.writeText(state.plantumlCode);
      alert('PlantUML code copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      const textArea = document.createElement('textarea');
      textArea.value = state.plantumlCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('PlantUML code copied to clipboard!');
    }
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setCreationResult(null);
    setCreationError(null);
  };

  const handleConfluenceSubmit = async (spaceName, pageNameValue) => {
    console.log("CONFLUENCE SUBMIT", spaceName, pageNameValue);
    if (!selectedAccount || !spaceName.trim() || !pageNameValue.trim()) {
      alert('Please select an account, space, and enter a page name');
      return;
    }
    setIsCreating(true);
    setCreationResult(null);
    setCreationError(null);

    await handleAddToConfluence(selectedAccount, spaceName, pageNameValue);

    setSelectedAccount(null);
    setConfluenceSpaceName('INTELOG-TEST-SPACE');
    setPageName('');
  };

  const handleAddToConfluence = async (account, spaceName, pageNameValue) => {
    console.log("ADDING TO CONFLUENCE", spaceName, pageNameValue);
    if (!state.plantumlCode || !account) return;

    try {
      const response = await fetch(`${backendUrl}/api/v1/llm-mcp-connector`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': localStorage.getItem('token')
        },
        body: JSON.stringify({
          title: pageNameValue || projName,
          plantumlContent: state.plantumlCode,
          spaceKey: spaceName,
          baseUrl: account.confluenceBaseUrl,
          email: account.confluenceEmail,
          apiToken: account.confluencePat,
          type: 'design',
          content: {
            description: state.description,
            plantumlCode: state.plantumlCode,
            documentation: state.documentation.markdown,
            userRequest: state.chat,
            metadata: {
              diagramType: state.diagramResult?.data?.diagramType || 'sequence',
              componentCount: state.components.length,
              interactionCount: state.interactions.length,
              sessionId: state.diagramResult?.requestId,
              generatedAt: state.documentation.generatedAt,
              tokenCost: state.documentation.tokenCost
            }
          }
        }),
      });

      const result = await response.json();
      console.log('Full response from backend:', result);

      if (result.data?.pageDetails?.url) {
        setCreationResult({
          pageUrl: result.data.pageDetails.url,
          pageId: result.data.pageDetails.pageId,
          pageTitle: result.data.pageDetails.title || projName,
          space: result.data.pageDetails.space || confluenceSpaceName,
          message: result.data.message,
          fullMessage: result.data.message
        });

        console.log('✅ Confluence page created:', result.data.pageDetails);
      } else {
        throw new Error('Page creation failed');
      }

      console.log('✅ Confluence page created:', result);

    } catch (error) {
      console.error('❌ Error creating Confluence page:', error);
      setCreationError(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const downloadPlantUML = () => {
    if (!state.plantumlCode) return;

    const blob = new Blob([state.plantumlCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagram-${sessionId ? sessionId.slice(0, 8) : 'unknown'}.puml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleNewSession = async () => {
    const currentSessionId = sessionId || localStorage.getItem('langraph_session_id');

    console.log("Clearing memory for session:", currentSessionId);

    if (currentSessionId) {
      try {
        const response = await fetch(`${domain}/api/v1/design/session/${currentSessionId}/memory`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': localStorage.getItem('token'),
            'X-Session-ID': currentSessionId
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Server memory cleared successfully:", result);
        } else {
          console.error("Failed to clear server memory:", response.status);
        }
      } catch (error) {
        console.error("Error clearing server memory:", error);
      }
    }

    const newSessionId = createNewSession();

    console.log('Session reset. New session ID:', newSessionId);

    setState(prevState => ({
      ...prevState,
      chat: '',
      diagramResult: null,
      diagramError: null,
      plantumlCode: '',
      components: [],
      interactions: [],
      loading: false,
      requestCount: 0,
      sessionHistory: []
    }));

    // ✅ Clear design logs
    setDesignLogs([]);

    localStorage.removeItem('chatResponse');
    localStorage.removeItem('plantUML');
    localStorage.removeItem('langraph_session_history');
    localStorage.removeItem('langraph_session_request_count');

    setSessionData({
      createdAt: new Date().toISOString(),
      requestCount: 0
    });
  };

  const createNewSession = useCallback(() => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);

    localStorage.setItem('langraph_session_id', newSessionId);
    localStorage.setItem('langraph_session_created', new Date().toISOString());

    console.log('New session created:', newSessionId);
    return newSessionId;
  }, []);

  useEffect(() => {
    // ✅ Prevent double initialization
    if (hasInitialized.current) {
      console.log('⚠️ Already initialized, skipping...');
      return;
    }

    hasInitialized.current = true;

    var socketUrl = url.SOCKET;

    // ✅ Check if socket already exists
    if (socketRef.current) {
      console.log('⚠️ Socket already exists, reusing...');
      return;
    }

    const socket = socketIOClient(socketUrl, { rejectUnauthorized: true, transports: ['websocket'] });
    socketRef.current = socket;

    // ✅ Socket connection handlers
    const handleConnect = () => {
      console.log('✅ Socket connected:', socket.id);
      setSocketConnected(true);

      setDesignLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'success',
        message: 'Connected to design workspace',
        type: 'system'
      }]);
    };

    const handleDisconnect = (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setSocketConnected(false);

      setDesignLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'warning',
        message: `Connection lost • ${reason}`,
        type: 'system'
      }]);
    };

    const handleConnectError = (error) => {
      console.error('❌ Socket connection error:', error);

      setDesignLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Connection failed • ${error.message}`,
        type: 'system'
      }]);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    const existingSessionId = localStorage.getItem('langraph_session_id');
    const existingSessionCreated = localStorage.getItem('langraph_session_created');
    const existingHistory = JSON.parse(localStorage.getItem('langraph_session_history') || '[]');

    if (existingSessionId && existingSessionCreated) {
      const savedRequestCount = localStorage.getItem('langraph_session_request_count');
      setSessionId(existingSessionId);
      setSessionData({
        createdAt: existingSessionCreated,
        requestCount: savedRequestCount ? parseInt(savedRequestCount) : 0
      });
      setState(prevState => ({
        ...prevState,
        sessionHistory: existingHistory
      }));
    } else {
      createNewSession();
    }

    // Fetch Confluence accounts
    fetch(domain + '/api/v1/confluence', {
      headers: {
        'x-access-token': localStorage.getItem('token')
      }
    })
      .then(response => response.json())
      .then(data => {
        console.log("CONFLUENCE", data)
        if (data) {
          setConfluenceAccounts(data || []);
        }
      })
      .catch(error => console.error('Error fetching Confluence accounts:', error));

    // ✅ Cleanup
    return () => {
      if (socketRef.current) {
        console.log('🧹 Cleaning up socket connection');
        socketRef.current.off('connect', handleConnect);
        socketRef.current.off('disconnect', handleDisconnect);
        socketRef.current.off('connect_error', handleConnectError);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [createNewSession]);

  const submit = () => {
    const newRequestCount = (sessionData?.requestCount || 0) + 1;

    setSessionData(prevData => ({
      ...prevData,
      requestCount: newRequestCount
    }));

    localStorage.setItem('langraph_session_request_count', newRequestCount.toString());

    setState(prevState => ({
      ...prevState,
      loading: true,
      diagramResult: null,
      diagramError: null,
      plantumlCode: '',
      components: [],
      interactions: [],
    }));

    // ✅ Add generation start log
    setDesignLogs(prev => [...prev, {
      timestamp: new Date().toISOString(),
      level: 'info',
      message: '🔨 Starting diagram generation...',
      type: 'generation'
    }]);

    const currentRequest = state.chat;

    var postObj = {
      request: state.chat,
      sessionId: sessionId,
      useMemory: true
    }

    console.log("post design request", postObj)
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
      body: JSON.stringify(postObj)
    };

    fetch(domain + '/api/v1/design/generate', requestOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log("Full response:", data);

        if (data.success && data.data) {
          setState(prevState => ({
            ...prevState,
            loading: false,
            diagramResult: data,
            plantumlCode: data.data.plantumlCode || '',
            components: data.data.components || [],
            interactions: data.data.interactions || [],
            processingTime: data.processingTime,
            diagramError: null,
            documentation: data.data.documentation || '',
            html: data.data.html || '',
            markdown: data.data.markdown || '',
            description: data.data.description || ''
          }));

          // ✅ Add success log
          setDesignLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            level: 'success',
            message: `🎉 Diagram generated successfully • ${data.data.components?.length || 0} components • ${Math.round(data.processingTime / 1000)}s`,
            type: 'generation'
          }]);

          addToSessionHistory(currentRequest, data);

        } else {
          setState(prevState => ({
            ...prevState,
            loading: false,
            diagramError: data.error || 'Unknown error occurred',
            diagramResult: null
          }));

          // ✅ Add error log
          setDesignLogs(prev => [...prev, {
            timestamp: new Date().toISOString(),
            level: 'error',
            message: `❌ Generation failed • ${data.error || 'Unknown error'}`,
            type: 'generation'
          }]);

          addToSessionHistory(currentRequest, null, data.error || 'Unknown error occurred');
        }
      })
      .catch(error => {
        console.error('Error:', error);
        setState(prevState => ({
          ...prevState,
          loading: false,
          diagramError: error.message,
          diagramResult: null
        }));

        // ✅ Add error log
        setDesignLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          level: 'error',
          message: `❌ Request failed • ${error.message}`,
          type: 'generation'
        }]);

        addToSessionHistory(currentRequest, null, error.message);
      });
  }

const getModalBody = () => {
  return (
    <>
      {/* ========================================
        TWO-COLUMN LAYOUT (Center + Right)
        NO TOP HEADER - Just the two columns
      ======================================== */}
      <div className="design-two-column-layout">

        {/* ========================================
          CENTER COLUMN - Generated Design (Full Width)
        ======================================== */}
        <div className="center-column-design">
          <div className="center-column-header">
            <h3>Generated Design</h3>

            <div className="header-actions">
              {/* ✅ NEW SESSION BUTTON (Always visible) */}
              <button 
                className="new-session-btn-header" 
                onClick={handleNewSession}
                title="Start New Session"
              >
                ➕ New Session
              </button>

              {state.plantumlCode && (
                <>
                  <div className="view-toggle-container">
                    <button
                      onClick={() => setState(prev => ({ ...prev, activeView: 'diagram' }))}
                      className={`view-toggle-btn ${state.activeView === 'diagram' ? 'active' : ''}`}
                    >
                      👁 Diagram
                    </button>
                    <button
                      onClick={() => setState(prev => ({ ...prev, activeView: 'code' }))}
                      className={`view-toggle-btn ${state.activeView === 'code' ? 'active' : ''}`}
                    >
                      🔧 Code
                    </button>
                  </div>

                  {state.activeView === 'code' && (
                    <div className="code-actions">
                      <button onClick={copyToClipboard} className="icon-btn" title="Copy">
                        📋
                      </button>
                      <button onClick={downloadPlantUML} className="icon-btn" title="Download">
                        💾
                      </button>
                    </div>
                  )}

                  <div className="confluence-dropdown-container">
                    <button
                      onClick={() => setShowConfluenceDropdown(!showConfluenceDropdown)}
                      className="icon-btn"
                      title="Add to Confluence"
                    >
                      🔗
                    </button>

                    {showConfluenceDropdown && (
                      <div className="confluence-dropdown">
                        {confluenceAccounts.length > 0 ? (
                          <>
                            {!creationResult && !creationError && (
                              <>
                                <div className="confluence-section">
                                  <div className="confluence-section-title">
                                    SELECT ACCOUNT
                                  </div>
                                  {confluenceAccounts.map((account) => (
                                    <div
                                      key={account._id}
                                      onClick={() => !isCreating && handleAccountSelect(account)}
                                      className={`confluence-account-item ${selectedAccount?._id === account._id ? 'selected' : ''
                                        } ${isCreating ? 'disabled' : ''}`}
                                    >
                                      {selectedAccount?._id === account._id && '✓ '}
                                      {account.confluenceEmail}
                                    </div>
                                  ))}
                                </div>

                                {selectedAccount && (
                                  <ConfluenceSection
                                    selectedAccount={selectedAccount}
                                    onCreatePage={(data) => {
                                      setConfluenceSpaceName(data.spaceKey);
                                      setPageName(data.pageName);
                                      handleConfluenceSubmit(data.spaceKey, data.pageName);
                                    }}
                                  />
                                )}
                              </>
                            )}

                            {creationResult && (
                              <div className="confluence-result success">
                                <div className="result-icon">✅</div>
                                <a href={creationResult.pageUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="confluence-link"
                                >
                                  Open Page ↗
                                </a>
                                <button className="confluence-close" onClick={handleReset}>
                                  Close
                                </button>
                              </div>
                            )}

                            {creationError && (
                              <div className="confluence-result error">
                                <div className="result-icon">❌</div>
                                <div className="error-text">{creationError}</div>
                                <button className="confluence-close" onClick={handleReset}>
                                  Close
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="confluence-empty">No accounts configured</div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="center-column-body">
            {state.diagramError && !state.plantumlCode && !state.loading ? (
              <div className="error-state">
                <div className="error-icon">⚠</div>
                <h3>Generation Failed</h3>
                <p>{state.diagramError}</p>
              </div>
            ) : state.plantumlCode ? (
              <div className="result-content">
                {state.activeView === 'diagram' ? (
                  <ZoomableDiagram
                    plantumlCode={state.plantumlCode}
                    plantumlEncoder={plantumlEncoder}
                    onCodeUpdate={handleCodeUpdate}
                  />
                ) : (
                  <div className="code-view-container">
                    <div className="code-view-header">
                      <span>PlantUML Source</span>
                      <span className="code-lines">
                        {state.plantumlCode.split('\n').length} lines
                      </span>
                    </div>
                    <div className="code-view-content">
                      <pre><code>{state.plantumlCode}</code></pre>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                {state.loading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Generating design...</p>
                  </div>
                ) : (
                  <div className="empty-state-content">
                    <div className="empty-icon">📊</div>
                    <p className="empty-title">No design generated</p>
                    <p className="empty-subtitle">Enter a prompt to get started →</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {state.diagramResult && state.plantumlCode && (
            <div className="center-column-footer">
              <div className="metadata-item">
                <span className="label">Type:</span>
                <span className="value">{state.diagramResult.data.diagramType}</span>
              </div>
              <div className="metadata-item">
                <span className="label">Components:</span>
                <span className="value">{state.components.length}</span>
              </div>
              <div className="metadata-item">
                <span className="label">Interactions:</span>
                <span className="value">{state.interactions.length}</span>
              </div>
              <div className="metadata-item">
                <span className="label">Time:</span>
                <span className="value">{Math.round(state.processingTime / 1000)}s</span>
              </div>
            </div>
          )}
        </div>

        {/* ========================================
          RIGHT COLUMN - Console + Prompt
        ======================================== */}
        <ConsolePromptPanel
          socketConnected={socketConnected}
          integrationLogs={designLogs}
          deploymentLogs={[]}
          onClearLogs={() => setDesignLogs([])}
          promptValue={state.chat}
          onPromptChange={(value) => setState(prev => ({ ...prev, chat: value }))}
          promptPlaceholder="Describe your design...

e.g., 'Create a sequence diagram for payment processing'"
          onSubmit={submit}
          isLoading={state.loading}
          loadingText="Generating..."
          submitButtonText="Generate Design"
          progress={{ step: state.loading ? 'Generating diagram...' : '', percent: state.loading ? 50 : 0 }}
          examples={samplePrompts}
          onExampleClick={(example) => setState(prev => ({ ...prev, chat: example.prompt }))}
          showCharCount={true}
        />
      </div>
    </>
  );
};

  console.log("state", state)
  return (
  <div className="root">
    <CustomHeaderWithOutAppBar />
    <main className="content">
      {!state.showModal && getModalBody()}
    </main>
  </div>
);
}

export default DesignAgent;